import {
    UnstyledButton,
    Modal,
    NumberInput,
    TextInput,
    Button,
    Group,
    Switch,
    Select,
    Alert,
    Tooltip,
} from '@mantine/core'

import classNames from 'classnames'

import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Bus
import { useBus } from 'react-bus'

// Icon
import { IconAlertCircleFilled, IconPencilCog } from '@tabler/icons-react'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'

// UI
import SKU from '@/components/ui/SKU'
import Banner from '@/components/ui/Banner'

export default function ComponentInvlevelInList({
    component,
    invlevel,
    invtype = 'physical',
    onInvlevelChange,
    location_id = 1,
    size = 'normal',
}) {
    const bus = useBus()
    const disabled = location_id === 'all'

    const textClasses = classNames('text-sm shrink-0', {
        'text-green-500 font-medium': invlevel > 0,
        'text-yellow-500 font-medium':
            invlevel !== null && invlevel <= 10 && invlevel > 0,
        'text-gray-400': invlevel === null,
        'text-red-500 font-medium': invlevel !== null && invlevel <= 0,
    })

    let textLabel = ''

    if (size !== 'small') {
        if (invlevel === null) {
            textLabel = invtype === 'physical' ? 'Non stocké' : 'Non suivi'
        } else {
            const formatedInvlevel = new Intl.NumberFormat('fr-FR').format(
                invlevel
            )

            textLabel = formatedInvlevel + ' en stock'
        }
    } else {
        const formatedInvlevel = new Intl.NumberFormat('fr-FR').format(invlevel)

        textLabel = invlevel === null ? '-' : formatedInvlevel
    }

    // Modal content
    const [opened, { open, close }] = useDisclosure(false)
    const [newInvlevel, setNewInvelevel] = useState(invlevel ?? 0)
    const [newEvolution, setNewEvolution] = useState(0)
    const [invReason, setInvReason] = useState('manual')
    const [invComment, setInvComment] = useState('')

    const [isInvlevelEnabled, setIsInvlevelEnabled] = useState(
        invlevel !== null ? true : false
    )

    const handleInvlevelChange = (value) => {
        setNewInvelevel(value)

        // Calculate new evolution

        if (value === null) {
            setNewEvolution(0 - invlevel)
        }

        if (value !== null) {
            setNewEvolution(value - invlevel)
        }
    }

    const handleNewEvolutionChange = (value) => {
        // Convert to number
        const newValue = Number(value)
        setNewEvolution(newValue)

        // Calculate new invlevel
        if (newValue !== null) {
            setNewInvelevel(invlevel + newValue)
        }
    }

    const openAndReset = () => {
        open()
        setNewInvelevel(invlevel ?? 0)
        setNewEvolution(0)
        setInvReason('manual')
        setIsInvlevelEnabled(invlevel !== null ? true : false)
    }

    const hasChanged = () => {
        // If the component is enabled and is level has changed
        return newInvlevel !== invlevel && isInvlevelEnabled === true
    }

    const hasDisabled = () => {
        // If the component was stored but is now disabled
        return isInvlevelEnabled === false && invlevel !== null
    }

    const evolution = newInvlevel - invlevel

    const disabledLabel =
        invtype === 'physical'
            ? 'Désactiver le stockage'
            : 'Désactiver la vente'

    // Submit
    const { requestPost } = useAxiosApi()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const submitEvolution = async () => {
        setIsSubmitting(true)

        await requestPost(`/components/${component.id}/edit-inventory`, {
            location_id,
            evolution,
            invtype,
            reason: invReason,
            comment: invComment || undefined,
        })

        if (onInvlevelChange) {
            await onInvlevelChange()
        }

        setIsSubmitting(false)

        // Emit change
        bus.emit('componentChanged')

        close()
    }

    const submitDisable = async () => {
        setIsSubmitting(true)

        await requestPost(`/components/${component.id}/edit-inventory`, {
            location_id,
            evolution: 0 - invlevel,
            invtype,
            reason: 'manual',
        })

        if (onInvlevelChange) {
            await onInvlevelChange()
        }

        setIsSubmitting(false)
        close()
    }

    const full_name =
        (component?.component_category?.name_fr ??
            component?.component_categories?.name_fr ??
            component.name_fr) +
        ' — ' +
        component.name

    const { userLocationId, locations } = useUserLocation()

    const currentLocation = locations?.find((l) => l.id === location_id)
    const currentLocationName = currentLocation?.name

    // Current location
    const isAtDifferentLocation = userLocationId !== location_id

    return (
        <>
            <Modal
                opened={opened}
                onClose={close}
                size="lg"
                title={
                    invtype === 'physical'
                        ? 'Stock physique ' + currentLocationName
                        : 'Quantité en vente ' + currentLocationName
                }
                centered
            >
                <div className="flex flex-row gap-3 items-center">
                    <SKU component={component} size="sm" />
                    <div className="truncate">{full_name}</div>
                </div>

                <div className="flex flex-col gap-5 mt-5">
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <div
                                className={`p-4 flex flex-col gap-2 border border-gray-200 rounded-lg cursor-pointer transition-all hover:border-blue-500 text-gray-500 ${
                                    isInvlevelEnabled
                                        ? 'outline-solid outline-2 outline-blue-500 bg-blue-50 text-baltic'
                                        : ''
                                }`}
                                onClick={() => {
                                    setIsInvlevelEnabled(true)
                                }}
                            >
                                <span className="font-medium text-black text-sm">
                                    Stocké à l'emplacement
                                </span>

                                <span className="text-gray-500 text-sm">
                                    Le composant est stocké à cet emplacement.
                                </span>
                            </div>

                            <div
                                className={`p-4 flex flex-col gap-2 border border-gray-200 rounded-lg cursor-pointer transition-all hover:border-blue-500 text-gray-500 ${
                                    !isInvlevelEnabled
                                        ? 'outline-solid outline-2 outline-blue-500 bg-blue-50 text-baltic'
                                        : ''
                                }`}
                                onClick={() => {
                                    setIsInvlevelEnabled(false)
                                }}
                            >
                                <span className="font-medium text-black text-sm">
                                    Non stocké à l'emplacement
                                </span>

                                <span className="text-gray-500 text-sm">
                                    Le composant ne nécessite pas de suivi de
                                    stock.
                                </span>
                            </div>
                        </div>

                        {false && (
                            <Switch
                                checked={isInvlevelEnabled}
                                onChange={(e) => {
                                    setIsInvlevelEnabled(
                                        e.currentTarget.checked
                                    )
                                }}
                                label={
                                    invtype === 'physical'
                                        ? 'Activer le stockage à cet emplacement'
                                        : 'Activer la vente à cet emplacement'
                                }
                            />
                        )}

                        {isInvlevelEnabled && (
                            <div className="grid grid-cols-2 w-full gap-3">
                                <NumberInput
                                    value={newInvlevel}
                                    onChange={handleInvlevelChange}
                                    label="Niveau du stock"
                                    min={
                                        invtype === 'physical' ? 0 : -999999999
                                    }
                                    clampBehavior="strict"
                                />

                                <NumberInput
                                    value={newEvolution}
                                    onChange={handleNewEvolutionChange}
                                    label="Ajustement"
                                    prefix={newEvolution > 0 ? '+' : ''}
                                />
                            </div>
                        )}

                        {isInvlevelEnabled && invtype === 'physical' && (
                            <Select
                                allowDeselect={false}
                                label="Type de mouvement"
                                value={invReason}
                                onChange={(value) => setInvReason(value)}
                                data={[
                                    {
                                        value: 'manual',
                                        label: 'Ajustement manuel',
                                    },
                                    {
                                        value: 'inventory',
                                        label: 'Inventaire (le stock indiqué doit être absolulement exact)',
                                    },
                                    {
                                        value: 'reception',
                                        label: 'Réception',
                                    },
                                ]}
                            />
                        )}

                        {isInvlevelEnabled &&
                            invtype === 'physical' &&
                            invReason === 'manual' && (
                                <TextInput
                                    label="Commentaire"
                                    placeholder="Raison de l'ajustement manuel (optionnel)"
                                    maxLength={150}
                                    value={invComment}
                                    onChange={(e) =>
                                        setInvComment(e.currentTarget.value)
                                    }
                                />
                            )}
                    </div>

                    {isAtDifferentLocation && (
                        <Banner title="Emplacement différent" type="error">
                            Attention, vous modifiez le stock d'un emplacement
                            différent de votre emplacement actuel.
                        </Banner>
                    )}

                    <Group grow spacing="xs">
                        <Button variant="default" onClick={close}>
                            Annuler
                        </Button>

                        {!hasDisabled() && (
                            <Button
                                disabled={
                                    !hasChanged() && invReason !== 'inventory'
                                }
                                onClick={submitEvolution}
                                loading={isSubmitting}
                            >
                                {invtype === 'physical'
                                    ? 'Enregistrer le stock'
                                    : 'Enregistrer la quantité'}
                            </Button>
                        )}

                        {hasDisabled() && (
                            <Button
                                color="red"
                                onClick={submitDisable}
                                loading={isSubmitting}
                            >
                                {disabledLabel}
                            </Button>
                        )}
                    </Group>
                </div>
            </Modal>

            {disabled === false && (
                <Tooltip label={'Modifier le stock : ' + currentLocationName}>
                    <UnstyledButton
                        onClick={openAndReset}
                        px="xl"
                        fz="sm"
                        className="h-full w-full flex items-center justify-between group"
                    >
                        <span className={textClasses}>{textLabel}</span>
                        <IconPencilCog
                            className="opacity-0 group-hover:opacity-40"
                            size={12}
                            stroke={1.6}
                        />
                    </UnstyledButton>
                </Tooltip>
            )}

            {disabled === true && (
                <div className="h-full w-full text-sm">
                    <span className={textClasses}>{textLabel}</span>
                </div>
            )}
        </>
    )
}
